/**
 * Temp document scopes of a workflow being edited.
 *
 * Documents added while editing a workflow do not live in the client model: Sun keeps them in
 * memory (`DocumentService.tempDocumentsMap`) keyed by the TEMP UUID the front end minted for the
 * reference they belong to (a task's uuid, a job node's id, a feature...). That mapping travels in
 * `workflow.tempUUIDMap` (`[{ tempUUID, referenceUUID }]`), and every document read for a reference
 * inside the workflow (`document/getAll/<resourceType>/<referenceId>/<tempUUID>`) merges the temp
 * documents stored under that temp UUID on top of the saved ones.
 *
 * Two consequences drive this module:
 *
 * - Abandoning an edit (revert, or leaving edit mode without saving) must DROP those temp UUIDs.
 *   Sun never deletes them and there is no endpoint to discard them, so forgetting them client-side
 *   is what makes the reads fall back to the saved documents — the same move `buildRevertedWorkflow`
 *   makes for a BOM imported during the discarded edit (HU-13).
 * - Asking Sun whether an edit has pending documents (`document/checkTemp`) must send the TEMP
 *   UUIDs: the endpoint intersects the received list with the keys of `tempDocumentsMap`, which are
 *   temp UUIDs, never reference UUIDs.
 *
 * Both apply to the whole tree: Sun collects the temp UUID map of every nested subworkflow when it
 * promotes the temp documents on save (`WorkflowRest.getTempUUIDHashMap`), so a scope left behind in
 * a subworkflow is as alive as one left behind at the root.
 *
 * @typedef {import('../../generic/utilities/objectStructures/workflow/workflow').Workflow} Workflow Workflow object
 */

/**
 * Every temp document scope UUID declared by the workflow and its nested subworkflows.
 * @param {Workflow} workflow Workflow being edited
 * @returns {string[]} Temp UUIDs, as expected by `document/checkTemp`
 */
const collectTempDocumentUUIDs = (workflow) => {
  if (!workflow) {
    return []
  }
  const tempUUIDs = (workflow.tempUUIDMap || []).map(uuidMap => uuidMap.tempUUID).filter(Boolean)
  const nodes = workflow.nodes || []
  return nodes.reduce((collected, node) => {
    return node.subWorkflow ? collected.concat(collectTempDocumentUUIDs(node.subWorkflow)) : collected
  }, tempUUIDs)
}

/**
 * Forgets every temp document scope of the workflow and its nested subworkflows, so the documents
 * added during a discarded edit stop being read back in place of the saved ones.
 * @param {Workflow} workflow Workflow to clean, mutated in place as the reducer already does
 * @returns {Workflow} The same workflow, for chaining
 */
const clearTempDocumentScopes = (workflow) => {
  if (!workflow) {
    return workflow
  }
  workflow.tempUUIDMap = []
  const nodes = workflow.nodes || []
  nodes.forEach(node => {
    if (node.subWorkflow) {
      clearTempDocumentScopes(node.subWorkflow)
    }
  })
  return workflow
}

export { collectTempDocumentUUIDs, clearTempDocumentScopes }
